import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTourPageReviews } from '@/hooks/useTourPageReviews';

export default function TripAdvisorCarousel() {
  const { reviews, loading, error, refresh } = useTourPageReviews();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('[data-review-card]')?.clientWidth || 300;
    const gap = 16;
    scrollRef.current.scrollBy({
      left: dir === 'left' ? -(cardWidth + gap) : cardWidth + gap,
      behavior: 'smooth',
    });
  };

  return (
    <section className="w-full bg-primary-100 py-8 md:py-12 lg:pb-12 lg:pt-0">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="mb-6 flex flex-col items-center text-center lg:mb-10">
          <h2 className="text-[18px] mb-2 font-extrabold uppercase leading-[1.05] tracking-tight text-foreground-950 sm:text-[38px] lg:text-[30px]">
            {t('carousel.howEarn')}
          </h2>
          <p className="-mt-1 text-[62px] mb-2 font-extrabold uppercase leading-none tracking-tight text-primary-700 sm:text-[108px] lg:text-[78px]">
            {t('carousel.star')}
          </p>
          <div className="-mt-1 flex items-center gap-2 sm:gap-3">
            <span className="text-[18px] font-extrabold uppercase leading-none text-foreground-950 sm:text-[34px] lg:text-[30px]">
              {t('carousel.on')}
            </span>
            <div className="flex items-center">
              <div className="w-7 h-7 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-primary-500 rounded-full flex items-center justify-center shrink-0">
                <i className="ri-star-fill text-white text-xs sm:text-sm" />
              </div>
              <span className="text-[18px] font-bold leading-none text-foreground-950 sm:text-[26px] lg:text-[24px] ml-1.5">
                Tripadvisor
              </span>
            </div>
          </div>
        </div>

        {/* Carousel */}
        {loading ? (
          <div className="flex gap-3 lg:gap-4 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="min-w-0 shrink-0 basis-[85%] sm:basis-[55%] md:basis-[40%] lg:basis-[25%]"
              >
                <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white">
                  <div className="aspect-[249/160] w-full animate-pulse bg-background-100" />
                  <div className="flex flex-col gap-3 px-3 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-background-100" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-2/3 animate-pulse rounded bg-background-100" />
                        <div className="h-2 w-1/3 animate-pulse rounded bg-background-100" />
                      </div>
                    </div>
                    <div className="h-3 w-full animate-pulse rounded bg-background-100" />
                    <div className="h-3 w-5/6 animate-pulse rounded bg-background-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-background-200/70 bg-white py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-star-line text-secondary-400 text-xl" />
            </div>
            <p className="text-sm text-secondary-500">{t('carousel.error')}</p>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              {t('carousel.tryAgain')}
            </button>
          </div>
        ) : (
          <div className="relative">
            <div
              ref={scrollRef}
              className="flex gap-3 lg:gap-4 overflow-x-auto hide-scrollbar scroll-smooth pb-2 -ml-3 pl-3 lg:-ml-4 lg:pl-4"
            >
              {reviews.map((review) => (
                <article
                  key={review.id}
                  data-review-card
                  className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-[55%] md:basis-[40%] lg:basis-[25%] pl-0"
                >
                  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-[0_2px_12px_-6px_rgba(15,23,42,0.12)]">
                    <div className="relative aspect-[249/160] w-full overflow-hidden">
                      <img
                        src={review.photo}
                        alt={`${review.author} review photo`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-1 flex-col px-3 py-3 lg:px-4 lg:py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <div className="relative h-12 w-12 overflow-hidden rounded-full ring-2 ring-white">
                            {review.avatar ? (
                              <img
                                src={review.avatar}
                                alt={review.author}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-primary-500 flex items-center justify-center text-xs font-semibold text-white">
                                {review.author.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center bg-primary-500 rounded-full">
                            <i className="ri-star-fill text-white text-[8px]" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[11px] font-semibold text-foreground-950">
                            {review.author}
                          </p>
                          <p className="text-[11px] text-secondary-500">{review.review_date}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-fill text-sm ${
                              i < review.rating ? 'text-accent-400' : 'text-secondary-200'
                            }`}
                          />
                        ))}
                      </div>
                      <h3 className="mt-3 text-[12px] font-semibold text-foreground-950">
                        {review.title}
                      </h3>
                      <p className="mt-1 text-[12px] leading-[1.55] text-secondary-600 line-clamp-4">
                        {expanded.has(review.id)
                          ? review.content
                          : review.content.slice(0, 200) +
                            (review.content.length > 200 ? '...' : '')}
                      </p>
                      {review.content.length > 200 && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(review.id)}
                          className="mt-1 self-start text-[12px] font-medium text-primary-600 hover:underline"
                        >
                          {expanded.has(review.id) ? t('carousel.showLess') : t('carousel.readMore')}
                        </button>
                      )}
                      <div className="mt-auto flex items-center gap-2 pt-3">
                        <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center shrink-0">
                          <i className="ri-star-fill text-white text-[8px]" />
                        </div>
                        <div className="text-[12px] leading-tight">
                          <p className="text-foreground-950">{t('carousel.postedOn')}</p>
                          <span className="font-medium text-primary-600 hover:underline cursor-pointer">
                            Tripadvisor
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Arrows */}
            <button
              type="button"
              aria-label={t('carousel.prev')}
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-md transition-all lg:left-0 lg:h-11 lg:w-11 lg:-translate-x-1/2 hover:bg-secondary-500"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              aria-label={t('carousel.next')}
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-md transition-all lg:right-0 lg:h-11 lg:w-11 lg:translate-x-1/2 hover:bg-secondary-500"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}